import { useParams } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

export default function Status() {
  useSeo({ title: "Job status", noindex: true });
  const { token } = useParams();
  const { data, refetch, isLoading } = useQuery({
    queryKey: ["status", token], queryFn: () => api.status(token), refetchInterval: 4000,
  });
  const [form, setForm] = useState({ app_url: "", platform: "mixed", cases_text: "", client_email: "", app_label: "" });
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const ALLOWED = ["csv", "xlsx", "docx", "md", "txt"];

  async function onPick(fileList) {
    const picked = Array.from(fileList || []);
    let count = files.length;
    for (const f of picked) {
      if (count >= 5) { toast.error("You can upload up to 5 files"); break; }
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`"${f.name}": use CSV, XLSX, DOCX, MD or TXT`); continue; }
      if (f.size > 5 * 1024 * 1024) { toast.error(`"${f.name}" is larger than 5 MB`); continue; }
      setUploading(true);
      try {
        const res = await api.uploadIntakeFile(token, f);
        setFiles((prev) => [...prev, res]);
        count += 1;
      } catch (err) { toast.error(err?.response?.data?.detail || `Could not read "${f.name}"`); }
      setUploading(false);
    }
    if (fileRef.current) fileRef.current.value = "";
  }

  async function removeFile(fid) {
    try { await api.deleteIntakeFile(token, fid); } catch (e) {}
    setFiles((prev) => prev.filter((x) => x.id !== fid));
  }

  if (isLoading) return <div className="app-main"><div className="wrap"><p>Loading…</p></div></div>;
  if (!data) return <div className="app-main"><div className="wrap"><h1>Invalid link</h1></div></div>;

  const job = data.job;
  const stepMap = { paid: 0, awaiting_submission: 1, planned: 2, dispatching: 2, in_progress: 2, verifying: 2, ready_for_release: 2, delivered: 3, delivered_report: 3 };
  const step = stepMap[job.state] ?? 1;

  async function submit(e) {
    e.preventDefault();
    if (!form.app_url || !form.client_email) { toast.error("Build link and email are required"); return; }
    setBusy(true);
    try {
      await api.intake(token, form);
      toast.success("Building your test plan…");
      refetch();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  }

  return (
    <>
      <header className="site-header"><div className="wrap"><a className="logo" href="/"><i></i>99tests</a>
        <nav><span className="muted small">Job {job.code} · {job.package}</span></nav></div></header>
      <main className="app-main"><div className="wrap">
        <ul className="progress">{[0, 1, 2, 3].map((i) => <li key={i} className={i < step ? "done" : i === step ? "now" : ""}></li>)}</ul>
        <div className="steps-inline"><span><b>Paid</b></span><span><b>Submit</b></span><span><b>Testing</b></span><span><b>Report</b></span></div>

        {job.state === "awaiting_submission" && (
          <div data-testid="intake-form">
            <h1 className="app-title" style={{ fontSize: "clamp(30px,4vw,46px)" }}>Tell us what to test.</h1>
            <p className="lede">Add the build, your test cases and a contact email — testers start as soon as the plan is ready.</p>
            <form className="panel-soft" style={{ padding: 28, marginTop: 28 }} onSubmit={submit}>
              <div className="field"><label>Build link or web URL</label>
                <input type="url" data-testid="intake-app-url" value={form.app_url} onChange={(e) => setForm({ ...form, app_url: e.target.value })} placeholder="https://staging.yourapp.com" /></div>
              <div className="field"><label>Platform</label>
                <select data-testid="intake-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                  <option value="android">Android</option><option value="ios">iOS</option><option value="web">Web</option><option value="mixed">Mixed</option></select></div>
              <div className="field"><label>Test cases (paste any format)</label>
                <textarea data-testid="intake-cases" value={form.cases_text} onChange={(e) => setForm({ ...form, cases_text: e.target.value })} placeholder={"Login with OTP\nSearch a product\nAdd to cart\nCheckout with UPI\nApply coupon"} style={{ minHeight: 140 }} /></div>

              <div className="field"><label>Upload test cases (optional)</label>
                <div data-testid="intake-dropzone"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); onPick(e.dataTransfer.files); }}
                  style={{ border: `2px dashed ${dragOver ? "#2563eb" : "#cbd5e1"}`, borderRadius: 12, padding: 22, textAlign: "center", cursor: "pointer", background: dragOver ? "#eff6ff" : "#f8fafc", transition: "background-color .15s, border-color .15s" }}>
                  <b style={{ display: "block" }}>Drag &amp; drop or click to choose files</b>
                  <span className="muted small">CSV, XLSX, DOCX, MD, TXT · up to 5 files · 5 MB each</span>
                  <input ref={fileRef} type="file" data-testid="intake-file-input" multiple accept=".csv,.xlsx,.docx,.md,.txt"
                    style={{ display: "none" }} onChange={(e) => onPick(e.target.files)} />
                </div>
                {uploading && <p className="hint" data-testid="intake-uploading">Reading file…</p>}
                {files.length > 0 && (
                  <div data-testid="intake-file-list" style={{ marginTop: 12, display: "grid", gap: 10 }}>
                    {files.map((f) => (
                      <div key={f.id} data-testid={`intake-file-${f.id}`} className="panel-soft" style={{ padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                          <span><b>{f.name}</b> <span className="muted small">· {(f.size / 1024).toFixed(0)} KB · {f.chars} chars</span></span>
                          <button type="button" data-testid={`intake-file-remove-${f.id}`} className="btn ghost small" onClick={() => removeFile(f.id)}>Remove</button>
                        </div>
                        <pre style={{ marginTop: 8, maxHeight: 120, overflow: "auto", whiteSpace: "pre-wrap", fontSize: 12, background: "#0f172a", color: "#e2e8f0", padding: 10, borderRadius: 8 }}>{f.extracted_text}</pre>
                      </div>
                    ))}
                  </div>
                )}
                <p className="hint">Uploaded files are combined with anything you paste above, then read by the planner.</p>
              </div>
              <div className="row">
                <div className="field"><label>Your email</label><input type="email" data-testid="intake-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="you@company.com" /></div>
                <div className="field"><label>App label (optional)</label><input type="text" value={form.app_label} onChange={(e) => setForm({ ...form, app_label: e.target.value })} placeholder="ShopEasy 4.12 checkout" /></div>
              </div>
              <div className="field"><label>Slack / Discord webhook (optional)</label>
                <input type="url" data-testid="intake-webhook" value={form.webhook_url || ""} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} placeholder="https://hooks.slack.com/services/…" />
                <p className="hint">We'll post to your channel when the plan is ready, when testing starts and when the report is delivered.</p></div>
              <button className="btn" data-testid="intake-submit" disabled={busy}>{busy ? "Building…" : "Build the test plan"}</button>
            </form>
          </div>
        )}

        {job.state !== "awaiting_submission" && (
          <div data-testid="status-view">
            <span className={`pill ${job.state === "delivered" ? "" : "blue"}`}>{job.state.replaceAll("_", " ")}</span>
            <h1 className="app-title" style={{ fontSize: "clamp(30px,4vw,46px)", marginTop: 12 }}>{job.app_label || job.code}</h1>
            {data.coverage && (
              <div className="kpis" style={{ marginTop: 24 }}>
                <div className="kpi"><b>{data.coverage.cases_parsed}</b><span>cases parsed</span></div>
                <div className="kpi"><b>{data.coverage.cases_covered}</b><span>cases covered</span></div>
                <div className="kpi"><b>{data.coverage.sessions}</b><span>sessions planned</span></div>
                <div className="kpi"><b>{data.findings_count}</b><span>findings so far</span></div>
              </div>
            )}
            {job.stalled_reason && <div className="empty" style={{ marginBottom: 20 }}><b>Job stalled: {job.stalled_reason}</b>Our operator is on it.</div>}
            {data.report_available && <p><a className="btn green" data-testid="view-report-btn" href={`/report/${token}`}>View your report</a></p>}
            <div className="panel" style={{ marginTop: 20, overflow: "hidden" }}>
              <div className="panel-head"><span>Sessions</span><span className="muted">grouped by device</span></div>
              <div className="panel-body" style={{ padding: 0 }}>
                <table className="tbl"><thead><tr><th>#</th><th>Device</th><th>Band</th><th>Cases</th><th className="c">Status</th></tr></thead>
                  <tbody>{data.sessions.map((s) => (
                    <tr key={s.slot_index}><td className="num">{s.slot_index}</td>
                      <td>{deviceLabel(s.device_requirement)}</td><td>{s.band === "DEEP" ? "90 min" : "45 min"}</td>
                      <td>{s.assigned_case_ids.length} cases</td>
                      <td className="c"><span className={`pill ${["submitted", "started", "accepted"].includes(s.state) ? "" : "neutral"}`}>{s.state}</span></td></tr>
                  ))}</tbody></table>
              </div>
            </div>
          </div>
        )}
      </div></main>
    </>
  );
}

function deviceLabel(req) {
  if (!req) return "—";
  if (req.platform === "web") return `${req.browser || "Browser"} on ${req.os || ""}`;
  return `${req.brand || ""} ${req.platform === "android" ? "Android" : "iOS"}${req.os_min ? " " + req.os_min + "+" : ""}`;
}
